import { gsap, EASE_OUT } from "@/lib/motion/gsap";
import { dirMul } from "@/lib/motion/dir";
import type { SiteMotion } from "@/lib/motion/site-runtime";
import {
  clearJumpOverlays,
  isSpineSection,
  restoreBootSection,
  transitionToSection,
  type SpineSection,
} from "@/lib/motion/section-deep-link";

/**
 * Seek the homepage spine to a named section (header / jump mid-session).
 * Overlay + waypoint seek + settleJump — no timed full rebuild by default.
 * Boot restore uses `restoreBootSection` instead.
 * Does not navigate to `/services` or `/work` — those are CTA-only subpages (ADR-020).
 */
export function seekToSection(m: SiteMotion, section: string) {
  if (!isSpineSection(section)) return;
  transitionToSection(m, section, {
    overlay: true,
    rebuild: false,
    unlock: true,
  });
}

/** @deprecated alias — prefer `restoreBootSection` from section-deep-link */
export function consumeHomeSectionQuery(m: SiteMotion) {
  restoreBootSection(m);
}

/**
 * Bind jump menu + header `.jump_link` seekers.
 * Returns teardown — must be called before re-bind (epoch / Fast Refresh).
 */
export function bindJumpMenu(m: SiteMotion): () => void {
  const menuSet = document.querySelector(".jump_menu_set");
  const items = document.querySelectorAll<HTMLElement>(".jump_menu_item");
  const links = document.querySelectorAll<HTMLElement>(".jump_link");
  if (!menuSet) return () => {};
  const root = menuSet;
  let menuTl: gsap.core.Timeline | null = null;
  const cleanups: Array<() => void> = [];

  function isMobileViewport() {
    return window.innerWidth <= 600;
  }

  function toggle(mode: "open" | "close") {
    menuTl?.kill();
    menuTl = gsap.timeline();
    const mobile = isMobileViewport();
    if (mode === "open") {
      m.body.classList.add("jump-menu-open");
      menuTl
        .set(".jump_menu_set", { overflow: "visible" }, 0)
        .set(".menu_overlay", { pointerEvents: "auto" }, 0)
        .to(".menu_overlay", { autoAlpha: 0.98, duration: 1, ease: EASE_OUT }, 0)
        .to(".jump_menu_arrow", { autoAlpha: 0, duration: 0.5, ease: EASE_OUT }, 0)
        .to(".jump_menu_line", { y: "500%", duration: 0.5, ease: EASE_OUT }, 0)
        .to(
          ".jump_menu_list",
          { y: mobile ? "-500%" : 0, duration: 0.5, ease: EASE_OUT },
          0,
        )
        .call(() => root.classList.add("opened"))
        .to(items, { autoAlpha: 1, duration: 1, ease: EASE_OUT }, 0.2);
      items.forEach((item, i) => {
        if (i !== m.jumpIndex) gsap.set(item, { autoAlpha: 0 });
      });
    } else {
      m.body.classList.remove("jump-menu-open");
      root.classList.remove("opened");
      items.forEach((item, i) => {
        if (i !== m.jumpIndex) gsap.to(item, { autoAlpha: 0, duration: 0.5, ease: EASE_OUT });
      });
      menuTl
        .to(".menu_overlay", { autoAlpha: 0, duration: 1, ease: EASE_OUT }, 0)
        .set(".menu_overlay", { pointerEvents: "none" })
        .to(".jump_menu_line", { y: 0, duration: 0.5, ease: EASE_OUT }, 0.2)
        .to(".jump_menu_list", { y: `${-100 * m.jumpIndex}%`, duration: 0.5, ease: EASE_OUT }, 0.2)
        .to(".jump_menu_arrow", { autoAlpha: 1, duration: 0.5, ease: EASE_OUT }, 0.2)
        .set(items, { autoAlpha: 1 })
        .set(".jump_menu_set", { overflow: "hidden" });
    }
  }

  function closeThenScroll(raw: string) {
    const section: SpineSection = isSpineSection(raw) ? raw : "hero";
    /*
     * Instantly collapse jump chrome. Animating menu_overlay closed while
     * transitionToSection fades site_overlay in left a window where Work /
     * Services planes showed through — and the close TL kept fighting settle.
     */
    menuTl?.kill();
    menuTl = null;
    m.body.classList.remove("jump-menu-open");
    root.classList.remove("opened");
    gsap.set(".menu_overlay", {
      autoAlpha: 0,
      pointerEvents: "none",
      overwrite: "auto",
    });
    gsap.set(items, { autoAlpha: 1, overwrite: "auto" });
    gsap.set(".jump_menu_arrow", { autoAlpha: 1, overwrite: "auto" });
    gsap.set(".jump_menu_line", { y: 0, overwrite: "auto" });
    gsap.set(".jump_menu_list", {
      y: `${-100 * m.jumpIndex}%`,
      overwrite: "auto",
    });
    gsap.set(".jump_menu_set", { overflow: "hidden", overwrite: "auto" });
    transitionToSection(m, section, {
      overlay: true,
      rebuild: false,
      unlock: true,
    });
  }

  const onItemClick = (e: Event) => {
    const item = e.currentTarget as HTMLElement;
    if (root.classList.contains("opened")) {
      e.stopPropagation();
      closeThenScroll(item.dataset.section || "");
    }
  };
  items.forEach((item) => {
    item.addEventListener("click", onItemClick);
    cleanups.push(() => item.removeEventListener("click", onItemClick));
    if (!m.isTouch) {
      const arrow = item.querySelector("svg");
      const enter = () => {
        if (arrow) gsap.to(arrow, { x: 6 * dirMul(), duration: 0.35, ease: EASE_OUT, overwrite: "auto" });
      };
      const leave = () => {
        if (arrow) gsap.to(arrow, { x: 0, duration: 0.35, ease: EASE_OUT, overwrite: "auto" });
      };
      item.addEventListener("mouseenter", enter);
      item.addEventListener("mouseleave", leave);
      cleanups.push(() => {
        item.removeEventListener("mouseenter", enter);
        item.removeEventListener("mouseleave", leave);
      });
    }
  });

  const onLinkClick = (e: Event) => {
    const link = e.currentTarget as HTMLElement;
    if (link.classList.contains("jump_menu_item")) return;
    e.preventDefault();
    closeThenScroll(link.dataset.section || "");
  };
  links.forEach((link) => {
    link.addEventListener("click", onLinkClick);
    cleanups.push(() => link.removeEventListener("click", onLinkClick));
  });

  if (m.isTouch) {
    const onRootClick = (e: Event) => {
      e.stopPropagation();
      toggle(root.classList.contains("opened") ? "close" : "open");
    };
    const onBodyClick = () => {
      if (root.classList.contains("opened")) toggle("close");
    };
    root.addEventListener("click", onRootClick);
    m.body.addEventListener("click", onBodyClick);
    cleanups.push(() => {
      root.removeEventListener("click", onRootClick);
      m.body.removeEventListener("click", onBodyClick);
    });
  } else {
    const onEnter = () => toggle("open");
    const onLeave = () => toggle("close");
    root.addEventListener("mouseenter", onEnter);
    root.addEventListener("mouseleave", onLeave);
    cleanups.push(() => {
      root.removeEventListener("mouseenter", onEnter);
      root.removeEventListener("mouseleave", onLeave);
    });
  }

  clearJumpOverlays();

  return () => {
    menuTl?.kill();
    menuTl = null;
    cleanups.forEach((fn) => fn());
  };
}
